<template>
  <conditional-context
    class="pricing-form-context"
    :show="isFormDisabled"
    :label="conditionalContextLabel"
    background="white"
    :additional-info="conditionalContextAdditionalInfo"
  >
    <validation-observer v-slot="{ passes, validate }" class="pricing-form">
      <b-form @submit.stop.prevent="passes(save)">
        <b-row>
          <b-col md="6">
            <forms-validated-input
              v-model="pricing.name"
              type="text"
              :name="`name_${pricing.id}`"
              :label="$t('pricings.fields.name')"
              :disabled="isFormDisabled || hasEnded"
              :rules="{
                required: true,
              }"
              @input="emitChange"
            />
            <b-row>
              <b-col md="6">
                <forms-validated-input
                  v-model="pricing.start_date"
                  type="date"
                  :name="`start_date_${pricing.id}`"
                  :label="$t('pricings.fields.start_date')"
                  :disabled-dates-fct="isBeforeNow"
                  :disabled="isFormDisabled || hasStarted"
                  :rules="{
                    required: true,
                  }"
                  @input="emitChange"
                />
              </b-col>
              <b-col md="6">
                <forms-validated-input
                  v-model="pricing.end_date"
                  type="date"
                  :name="`end_date_${pricing.id}`"
                  :label="$t('pricings.fields.end_date')"
                  :disabled-dates-fct="isBeforeNowOrStartDate"
                  :disabled="isFormDisabled || hasEnded"
                  :rules="{
                    required: false,
                  }"
                  @input="emitChange"
                />
              </b-col>
            </b-row>

            <forms-validated-input
              v-model="pricing.description"
              :disabled="isFormDisabled || hasEnded"
              type="textarea"
              :name="`description_${pricing.id}`"
              :label="$t('pricings.fields.description')"
              :max-rows="2"
              :rows="1"
              :placeholder="$t('pricings.placeholders.description')"
              :rules="{
                max: 250,
              }"
              @input="emitChange"
            />

            <forms-validated-input
              v-model="pricing.pricing_type"
              :disabled="isFormDisabled || hasStarted"
              type="select"
              :name="`pricing_type_${pricing.id}`"
              :options="pricingTypeOptions"
              :label="$t('pricings.fields.type')"
              :rules="{
                required: true,
              }"
              @input="emitChangeAndPrepareParametersAndFormula"
            />
            <b-alert
              v-if="exemptFromContributions && pricing.pricing_type === 'contribution'"
              variant="warning"
              show
            >
              {{ $t("components.pricing.pricing_form.exempt_warning") }}
            </b-alert>

            <conditional-context
              v-if="pricing.pricing_type === 'contribution'"
              show
              class="mt-4 mx-0"
              :label="$t('components.pricing.pricing_form.contribution_options')"
              background="white"
            >
              <forms-validated-input
                v-if="pricing.community_id"
                v-model="pricing.is_mandatory"
                :disabled="isFormDisabled || hasStarted"
                type="checkbox"
                :name="`is_mandatory_${pricing.id}`"
                :label="$t('pricings.fields.is_mandatory')"
                @input="emitChange"
              />
              <forms-validated-input
                v-else
                v-model="pricing.is_mandatory"
                :description="$t('pricings.descriptions.global_is_mandatory')"
                disabled
                type="checkbox"
                :name="`is_mandatory_${pricing.id}`"
                :label="$t('pricings.fields.is_mandatory')"
                @input="emitChange"
              />

              <forms-validated-input
                v-if="isGlobalAdmin"
                v-model="pricing.yearly_target_per_user"
                type="currency"
                :name="`yearly_target_per_user_${pricing.id}`"
                :disabled="isFormDisabled || hasStarted"
                :label="$t('pricings.fields.yearly_target')"
                @input="emitChange"
              />
            </conditional-context>
          </b-col>
          <b-col md="6">
            <conditional-context
              show
              class="mt-4 mx-0"
              :label="$t('components.pricing.pricing_form.targeted_vehicles')"
              background="white"
            >
              <forms-validated-input
                v-if="
                  pricing.pricing_type === 'insurance' || pricing.pricing_type === 'contribution'
                "
                v-model="pricing.loanable_ownership_type"
                type="select"
                :name="`loanable_ownership_type_${pricing.id}`"
                :disabled="isFormDisabled || hasStarted"
                :options="vehicleOwnershipOptions"
                :label="$t('components.pricing.pricing_form.shared_by')"
                :rules="{
                  required: true,
                }"
                @input="emitChange"
              />

              <b-form-group
                :label="$t('components.pricing.pricing_form.vehicle_types')"
                label-for="pricing_loanable_types"
                class="pricing-loanable-types"
              >
                <div class="position-relative">
                  <b-checkbox
                    v-if="showBikes"
                    class="group-select-all"
                    :checked="allBikeChecked"
                    :indeterminate="someBikeChecked"
                    :disabled="isFormDisabled || hasStarted"
                    @change="allBikesClicked"
                  >
                    {{ $t("components.pricing.pricing_form.all_bikes") }}
                  </b-checkbox>
                  <validation-provider
                    v-slot="{ validated, valid, errors }"
                    :name="$t('components.pricing.pricing_form.vehicle_types')"
                    :rules="{
                      required: true,
                    }"
                  >
                    <b-checkbox-group
                      v-if="showBikes"
                      v-model="pricing.pricing_loanable_types"
                      :options="bikeOptions"
                      class="checkbox-group"
                      :disabled="isFormDisabled || hasStarted"
                      @input="emitChange"
                    />

                    <b-checkbox-group
                      v-if="showTrailers"
                      v-model="pricing.pricing_loanable_types"
                      :disabled="isFormDisabled || hasStarted"
                      :options="trailerOptions"
                      class="checkbox-group"
                      @input="emitChange"
                    />

                    <div v-if="showCars" class="position-relative">
                      <b-checkbox
                        :disabled="isFormDisabled || hasStarted"
                        class="group-select-all"
                        :checked="allCarsChecked"
                        :indeterminate="someCarsChecked"
                        @change="allCarsClicked"
                      >
                        {{ $t("components.pricing.pricing_form.all_cars") }}
                      </b-checkbox>
                      <b-checkbox-group
                        v-model="pricing.pricing_loanable_types"
                        :disabled="isFormDisabled || hasStarted"
                        :options="carOptions"
                        class="checkbox-group"
                        @input="emitChange"
                      />
                    </div>

                    <b-checkbox-group
                      v-if="showCarTrailers"
                      v-model="pricing.pricing_loanable_types"
                      :disabled="isFormDisabled || hasStarted"
                      :options="carTrailerOptions"
                      class="checkbox-group"
                      @input="emitChange"
                    />

                    <b-form-invalid-feedback :state="validated ? valid : null">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </div>
              </b-form-group>
            </conditional-context>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <forms-validated-input
              v-model="pricing.rule"
              class="formula-input"
              type="textarea"
              :name="`rule_${pricing.id}`"
              :disabled="isFormDisabled || hasStarted"
              :label="$t('components.pricing.pricing_form.formula')"
              :max-rows="20"
              :rules="{
                required: true,
              }"
              @input="emitChange"
            />
            <b-alert v-if="pricing.pricing_type === 'parametrized_price'" variant="info" show>
              {{ $t("components.pricing.pricing_form.parametrized_price_instructions") }}

              <a v-if="!hasStarted" class="text-link cursor-pointer" @click="addDefaultFormula">{{
                $t("components.pricing.pricing_form.parametrized_price_default_formula")
              }}</a>
            </b-alert>
          </b-col>
        </b-row>

        <b-row v-if="pricing.pricing_type === 'parametrized_price'">
          <b-col>
            <conditional-context
              show
              class="mx-0"
              :label="capitalize($t('components.pricing.pricing_form.parametrized_price.distance'))"
              background="white"
            >
              <forms-validated-input
                v-model="pricing.parameters.distance.minimum"
                type="currency"
                :name="`distance_minimum_${pricing.id ?? 0}`"
                :label="capitalize($t('pricings.parameters.distance.minimum'))"
                :disabled="isFormDisabled || hasStarted"
                :rules="{
                  currency_min_value: 0,
                  currency_max_value: pricing.parameters.distance.current || 1000000,
                  required: true,
                }"
                @blur="validate"
                @input="emitChange"
              />

              <forms-validated-input
                v-model="pricing.parameters.distance.current"
                type="currency"
                :name="`distance_current_${pricing.id ?? 0}`"
                :label="capitalize($t('pricings.parameters.distance.default'))"
                :disabled="isFormDisabled || hasStarted"
                :rules="{
                  currency_min_value:
                    pricing.id || focused.distance_current
                      ? pricing.parameters.distance.minimum
                      : 0,
                  required: true,
                }"
                @blur="
                  focused.distance_current = true;
                  validate();
                "
                @input="emitChange"
              />

              <forms-validated-input
                v-model="pricing.parameters.distance.maximum"
                type="currency"
                :name="`distance_maximum_${pricing.id ?? 0}`"
                :label="capitalize($t('pricings.parameters.distance.maximum'))"
                :disabled="isFormDisabled || hasStarted"
                :rules="{
                  currency_min_value:
                    pricing.id || focused.distance_maximum
                      ? pricing.parameters.distance.current
                      : 0,
                  required: true,
                }"
                @blur="
                  focused.distance_maximum = true;
                  validate();
                "
                @input="emitChange"
              />
            </conditional-context>
          </b-col>

          <b-col>
            <conditional-context
              show
              class="mx-0"
              :label="capitalize($t('components.pricing.pricing_form.parametrized_price.hourly'))"
              background="white"
            >
              <forms-validated-input
                v-model="pricing.parameters.hourly.minimum"
                type="currency"
                :name="`hourly_minimum_${pricing.id ?? 0}`"
                :label="capitalize($t('pricings.parameters.hourly.minimum'))"
                :disabled="isFormDisabled || hasStarted"
                :rules="{
                  currency_min_value: 0,
                  currency_max_value: pricing.parameters.hourly.current || 1000000,
                  required: true,
                }"
                @blur="validate"
                @input="emitChange"
              />

              <forms-validated-input
                v-model="pricing.parameters.hourly.current"
                type="currency"
                :name="`hourly_current_${pricing.id ?? 0}`"
                :label="capitalize($t('pricings.parameters.hourly.default'))"
                :disabled="isFormDisabled || hasStarted"
                :rules="{
                  currency_min_value:
                    pricing.id || focused.hourly_current ? pricing.parameters.hourly.minimum : 0,
                  required: true,
                }"
                @blur="
                  focused.hourly_current = true;
                  validate();
                "
                @input="emitChange"
              />

              <forms-validated-input
                v-model="pricing.parameters.hourly.maximum"
                type="currency"
                :name="`hourly_maximum_${pricing.id ?? 0}`"
                :label="capitalize($t('pricings.parameters.hourly.maximum'))"
                :disabled="isFormDisabled || hasStarted"
                :rules="{
                  currency_min_value:
                    pricing.id || focused.hourly_maximum ? pricing.parameters.hourly.current : 0,
                  required: true,
                }"
                @blur="
                  focused.hourly_maximum = true;
                  validate();
                "
                @input="emitChange"
              />
            </conditional-context>
          </b-col>

          <b-col>
            <conditional-context
              show
              class="mx-0"
              :label="capitalize($t('components.pricing.pricing_form.parametrized_price.minimum'))"
              background="white"
            >
              <forms-validated-input
                v-model="pricing.parameters.minimum.minimum"
                type="currency"
                :name="`minimum_minimum_${pricing.id ?? 0}`"
                :disabled="isFormDisabled || hasStarted"
                :label="capitalize($t('pricings.parameters.minimum.minimum'))"
                :rules="{
                  currency_min_value: 0,
                  currency_max_value: pricing.parameters.minimum.current || 1000000,
                  required: true,
                }"
                @blur="validate"
                @input="emitChange"
              />

              <forms-validated-input
                v-model="pricing.parameters.minimum.current"
                type="currency"
                :name="`minimum_current_${pricing.id ?? 0}`"
                :disabled="isFormDisabled || hasStarted"
                :label="capitalize($t('pricings.parameters.minimum.default'))"
                :rules="{
                  currency_min_value:
                    pricing.id || focused.minimum_current ? pricing.parameters.minimum.minimum : 0,
                  required: true,
                }"
                @blur="
                  focused.minimum_current = true;
                  validate();
                "
                @input="emitChange"
              />

              <forms-validated-input
                v-model="pricing.parameters.minimum.maximum"
                type="currency"
                :name="`minimum_maximum_${pricing.id ?? 0}`"
                :disabled="isFormDisabled || hasStarted"
                :label="capitalize($t('pricings.parameters.minimum.maximum'))"
                :rules="{
                  currency_min_value:
                    pricing.id || focused.minimum_maximum ? pricing.parameters.minimum.current : 0,
                  required: true,
                }"
                @blur="
                  focused.minimum_maximum = true;
                  validate();
                "
                @input="emitChange"
              />
            </conditional-context>
          </b-col>
        </b-row>

        <icon-button
          v-if="!hasEnded"
          :loading="saving"
          role="save"
          type="submit"
          :disabled="saving || deleting || !pricing._changed || isFormDisabled"
        >
          {{ pricing.id ? $t("forms.save") : $t("forms.create") | capitalize }}
        </icon-button>
        <validated-button
          v-if="pricing.id && !hasStarted"
          class="ml-2"
          :disabled="saving || deleting || isFormDisabled"
          :action="deletePricing"
          variant="outline-danger"
          :label="capitalize($t('forms.archive'))"
          :question="$t('components.pricing.pricing_form.confirmations.archive')"
          icon="trash"
        />
        <icon-button
          v-if="pricing.id && !hasEnded"
          class="ml-2"
          :disabled="saving || deleting || isFormDisabled || !pricing._changed"
          role="reset"
          @click="reset"
        >
          {{ $t("forms.reset") | capitalize }}
        </icon-button>
        <icon-button v-if="!pricing.id" class="ml-2" role="remove-item" @click="deletePricing">
          {{ $t("forms.cancel") | capitalize }}
        </icon-button>
        <div class="mt-1">
          <span v-if="hasEnded" class="text-muted">
            {{ $t("components.pricing.pricing_form.status_messages.ended") }}
          </span>
          <span v-else-if="hasStarted" class="text-muted">
            {{ $t("components.pricing.pricing_form.status_messages.started") }}
          </span>
        </div>
      </b-form>
    </validation-observer>
  </conditional-context>
</template>

<script>
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import ConditionalContext from "@/components/shared/ConditionalContext.vue";
import IconButton from "@/components/shared/IconButton.vue";
import ValidatedButton from "@/components/shared/ValidatedButton.vue";
import { capitalize } from "@/helpers/filters";
import { isGlobalAdmin } from "@/helpers/permissions/users";
import { del, put } from "@/requests/server";
import dayjs from "dayjs";

const typeMapping = {
  car: ["car_small", "car_large", "car_small_electric", "car_large_electric"],
  bike: ["bike_regular", "bike_electric", "bike_cargo_regular", "bike_cargo_electric"],
  trailer: ["trailer"],
  car_trailer: ["car_trailer"],
};

export default {
  name: "PricingForm",
  components: { IconButton, ValidatedButton, ConditionalContext, FormsValidatedInput },
  props: {
    availableLoanableTypes: {
      type: Array,
      default: () => ["car", "bike", "trailer", "car_trailer"],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    exemptFromContributions: {
      type: Boolean,
      default: false,
    },
    initialPricing: {
      type: Object,
      default: undefined,
    },
    isPrivateCommunity: {
      type: Boolean,
      default: false,
    },
    onPricingSaved: {
      type: Function,
      default: undefined,
    },
    pricing: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      deleting: false,
      focused: {
        distance_current: false,
        distance_maximum: false,
        hourly_current: false,
        hourly_maximum: false,
        minimum_current: false,
        minimum_maximum: false,
      },
      saving: false,
      vehicleOwnershipOptions: [
        { value: "all", text: capitalize(this.$t("pricings.loanable_ownership_type.all")) },
        { value: "fleet", text: capitalize(this.$t("pricings.loanable_ownership_type.fleet")) },
        {
          value: "non_fleet",
          text: capitalize(this.$t("pricings.loanable_ownership_type.non_fleet")),
        },
      ],
    };
  },
  computed: {
    conditionalContextAdditionalInfo() {
      if (this.hasUnsupportedPricingTypeOptions || !this.isGlobalAdmin) {
        return this.$t("components.pricing.pricing_form.no_permissions");
      }

      if (this.disabled) {
        return this.$t("components.pricing.pricing_form.cant_edit_here");
      }

      return "";
    },
    conditionalContextLabel() {
      if (this.hasUnsupportedPricingTypeOptions || !this.isGlobalAdmin) {
        return this.$t("components.pricing.pricing_form.admin_only");
      }

      if (this.disabled) {
        return this.$t("components.pricing.pricing_form.edit_disabled");
      }

      return "";
    },
    isGlobalAdmin() {
      return isGlobalAdmin(this.$store.state.user);
    },
    isFormDisabled() {
      return this.disabled || this.hasUnsupportedPricingTypeOptions;
    },
    hasUnsupportedPricingTypeOptions() {
      return (
        this.pricing &&
        this.pricing.pricing_type &&
        !this.pricingTypeOptions.map((p) => p.value).includes(this.pricing.pricing_type)
      );
    },
    pricingTypeOptions() {
      const options = [
        {
          value: "contribution",
          text: capitalize(this.$t("pricings.types.contribution")),
        },
      ];

      if (this.isGlobalAdmin || this.isPrivateCommunity || this.pricing.is_global) {
        options.push({
          value: "price",
          text: capitalize(this.$t("pricings.types.price")),
        });
      }

      if (this.isGlobalAdmin || this.pricing.is_global) {
        options.push(
          {
            value: "insurance",
            text: capitalize(this.$t("pricings.types.insurance")),
          },
          {
            value: "parametrized_price",
            text: capitalize(this.$t("pricings.types.parametrized_price")),
          }
        );
      }
      return options;
    },
    showBikes() {
      return this.availableLoanableTypes.includes("bike");
    },
    showCars() {
      return this.availableLoanableTypes.includes("car");
    },
    showTrailers() {
      return this.availableLoanableTypes.includes("trailer");
    },
    showCarTrailers() {
      return this.availableLoanableTypes.includes("car_trailer");
    },
    bikeOptions() {
      return typeMapping.bike.map((t) => ({
        value: t,
        text: capitalize(this.$t(`pricings.loanableTypes.${t}`)),
      }));
    },
    carOptions() {
      return typeMapping.car.map((t) => ({
        value: t,
        text: capitalize(this.$t(`pricings.loanableTypes.${t}`)),
      }));
    },
    trailerOptions() {
      return typeMapping.trailer.map((t) => ({
        value: t,
        text: capitalize(this.$t(`pricings.loanableTypes.${t}`)),
      }));
    },
    carTrailerOptions() {
      return typeMapping.car_trailer.map((t) => ({
        value: t,
        text: capitalize(this.$t(`pricings.loanableTypes.${t}`)),
      }));
    },

    allBikeChecked() {
      return this.includesAll(this.pricing.pricing_loanable_types, typeMapping.bike);
    },
    allCarsChecked() {
      return this.includesAll(this.pricing.pricing_loanable_types, typeMapping.car);
    },
    someBikeChecked() {
      return (
        !this.allBikeChecked &&
        this.includesAny(this.pricing.pricing_loanable_types, typeMapping.bike)
      );
    },
    someCarsChecked() {
      return (
        !this.allCarsChecked &&
        this.includesAny(this.pricing.pricing_loanable_types, typeMapping.car)
      );
    },
    hasPricingChanged() {
      if (!this.initialPricing) {
        return true;
      }

      for (const pricingKey in this.pricing) {
        if (pricingKey.startsWith("_")) {
          continue;
        }
        if (pricingKey === "pricing_loanable_types") {
          const same =
            this.pricing.pricing_loanable_types.length ===
              this.initialPricing.pricing_loanable_types.length &&
            this.pricing.pricing_loanable_types.every((p) =>
              this.initialPricing.pricing_loanable_types.includes(p)
            );
          if (same) {
            continue;
          }

          return true;
        }

        if (this.pricing[pricingKey] !== this.initialPricing[pricingKey]) {
          return true;
        }
      }
      return false;
    },
    hasStarted() {
      return this.pricing.start_date && dayjs().isSameOrAfter(this.pricing.start_date, "day");
    },
    hasEnded() {
      return this.pricing.end_date && dayjs().isSameOrAfter(this.pricing.end_date, "day");
    },
  },
  methods: {
    capitalize,
    reset() {
      Object.assign(this.pricing, this.initialPricing);
      this.pricing._showDetails = true;
      this.emitChange();
    },
    isBeforeNow(date) {
      return dayjs(date).isSameOrBefore(dayjs(), "day");
    },
    isBeforeNowOrStartDate(date) {
      if (this.isBeforeNow(date)) {
        return true;
      }

      return this.pricing.start_date && dayjs(date).isSameOrBefore(this.pricing.start_date, "day");
    },
    allCarsClicked() {
      if (this.allCarsChecked) {
        this.updateLoanableTypes(
          this.pricing.pricing_loanable_types.filter((lt) => !typeMapping.car.includes(lt))
        );
      } else {
        this.updateLoanableTypes([
          ...new Set([...this.pricing.pricing_loanable_types, ...typeMapping.car]),
        ]);
      }
    },
    allBikesClicked() {
      if (this.allBikeChecked) {
        this.updateLoanableTypes(
          this.pricing.pricing_loanable_types.filter((lt) => !typeMapping.bike.includes(lt))
        );
      } else {
        this.updateLoanableTypes([
          ...new Set([...this.pricing.pricing_loanable_types, ...typeMapping.bike]),
        ]);
      }
    },
    includesAll(array, values) {
      for (const value of values) {
        if (!array.includes(value)) {
          return false;
        }
      }
      return true;
    },
    includesAny(array, values) {
      for (const value of values) {
        if (array.includes(value)) {
          return true;
        }
      }
      return false;
    },
    emitChange() {
      this.pricing._changed = this.hasPricingChanged;
      this.$emit("change", this.pricing);
    },
    emitChangeAndPrepareParametersAndFormula(value) {
      if (value === "parametrized_price") {
        if (!this.pricing.rule) {
          this.addDefaultFormula();
        }

        if (!this.pricing.parameters) {
          this.pricing.parameters = {
            distance: {
              minimum: 0,
              maximum: 0,
              current: 0,
            },
            hourly: {
              minimum: 0,
              maximum: 0,
              current: 0,
            },
            minimum: {
              minimum: 0,
              maximum: 0,
              current: 0,
            },
          };
        }
      }

      this.emitChange();
    },
    addDefaultFormula() {
      this.pricing.rule ||= "";

      if (this.pricing.rule) {
        this.pricing.rule += "\n";
      }

      this.pricing.rule += [
        "# Coût de la distance",
        "$COUT_DISTANCE = $KM * $TAUX_KM",
        "",
        "# Coût de la durée",
        "$COUT_DUREE = $HEURES * $TAUX_HORAIRE",
        "",
        "# Coût minimum (complète le coût durée et distance pour atteindre le minimum)",
        "$COUT_MINIMUM = MAX($MINIMUM - ($COUT_DUREE + $COUT_DISTANCE), 0)",
      ].join("\n");
    },
    updateLoanableTypes(loanableTypes) {
      this.pricing.pricing_loanable_types = loanableTypes;
      this.emitChange();
    },
    async save() {
      this.saving = true;
      if (!this.pricing.id) {
        // create
        const { data } = await put(`/pricings`, this.pricing, {
          notifications: {
            action: this.$t("components.pricing.pricing_form.notifications.create.action"),
            onSuccess: this.$t("components.pricing.pricing_form.notifications.create.success"),
          },
          cleanupCallback: () => (this.saving = false),
        });
        Object.assign(this.pricing, data);
        this.$emit("created");
      } else {
        // Save
        const { data } = await put(`/pricings/${this.pricing.id}`, this.pricing, {
          notifications: {
            action: this.$t("components.pricing.pricing_form.notifications.update.action"),
            onSuccess: this.$t("components.pricing.pricing_form.notifications.update.success"),
          },
          cleanupCallback: () => (this.saving = false),
        });
        Object.assign(this.pricing, data);
      }

      this.pricing._changed = false;
      if (this.onPricingSaved) {
        this.onPricingSaved(this.pricing);
      }
      this.$emit("change", this.pricing);
    },
    async deletePricing() {
      if (!this.pricing.id) {
        this.$emit("cancel-create");
        return;
      }
      this.deleting = true;
      await del(`/pricings/${this.pricing.id}`, {
        notifications: {
          action: this.$t("components.pricing.pricing_form.notifications.archive.action"),
          onSuccess: this.$t("components.pricing.pricing_form.notifications.archive.success"),
        },
        cleanupCallback: () => (this.deleting = false),
      });
      this.$emit("deleted");
    },
  },
};
</script>

<style lang="scss">
.pricing-form-context.conditional-context {
  margin: 0;
  padding-bottom: 1rem;
}
.pricing-form {
  .formula-input textarea {
    font-family: var(--font-family-monospace);
    white-space: pre-wrap;
  }

  .pricing-loanable-types {
    .group-select-all {
      display: inline-block;
      position: absolute;
      background: white;
      left: calc(0.75rem + 1px);
      top: -0.5rem;
      font-weight: bold;
    }

    .checkbox-group {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
      margin-top: 1rem;
      border-radius: 0.5rem;
      border: 1px solid $light-grey;
      padding: 1rem 0.75rem;
    }
  }
}
</style>
